/**
 * DrawerShell — shared chrome for right-side form drawers (Activities and
 * Services). A sheet with a 3px coral accent strip, a serif header, a
 * scrollable body and a sticky footer. `wide` bumps the sheet to 560px for
 * denser forms (e.g. the service product form).
 *
 * Promoted from Activities V2 (DESIGN_SPEC.md drawer chrome) when the
 * Services module adopted the same drawer-first form pattern.
 */

import type { ReactNode } from 'react';

import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from '@/theme/components/ui/sheet';
import { cn } from '@/lib/utils';

interface DrawerShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Sticky footer content (typically Cancel + primary action). */
  footer: ReactNode;
  /** 560px sheet instead of the default 480px. */
  wide?: boolean;
  children: ReactNode;
  className?: string;
}

export function DrawerShell({
  open,
  onOpenChange,
  title,
  subtitle,
  footer,
  wide,
  children,
  className,
}: DrawerShellProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          'flex w-full flex-col gap-0 border-l-0 bg-sand-50 p-0',
          wide ? 'sm:max-w-[560px]' : 'sm:max-w-[480px]',
          className,
        )}
        style={{ boxShadow: '-18px 0 48px rgba(28,20,10,.2)' }}
      >
        {/* coral accent strip */}
        <span aria-hidden="true" className="h-[3px] w-full shrink-0 bg-coral-500" />

        {/* header */}
        <div className="shrink-0 border-b border-sand-200 bg-sand-50 px-5 pb-4 pt-4 pr-12">
          <SheetTitle className="font-serif text-[19px] font-semibold tracking-[-0.01em] text-sand-900">
            {title}
          </SheetTitle>
          {subtitle ? (
            <SheetDescription className="mt-0.5 text-[12.5px] text-muted-foreground">
              {subtitle}
            </SheetDescription>
          ) : (
            <SheetDescription className="sr-only">{title}</SheetDescription>
          )}
        </div>

        {/* scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>

        {/* sticky footer */}
        <div className="flex shrink-0 items-center gap-2.5 border-t border-sand-200 bg-sand-50 px-5 py-3.5">
          {footer}
        </div>
      </SheetContent>
    </Sheet>
  );
}
